import { FlagContext } from '@unleash/proxy-client-react';
import { useContext } from 'react';
import { FeatureFlag } from '../feature-flags';
import { useConfig } from '@/shared/utils/config/config-context';
import { isAuthenticatedRamConfig } from '@/shared/env/runtime-config';
import type { ContextType } from 'react';
import type { IRamRuntimeConfig } from '@/shared/env/types';

type UnleashFlagContext = NonNullable<ContextType<typeof FlagContext>>;
type RamConfig = IRamRuntimeConfig | null | undefined;

function isUnleashConfigured(config: RamConfig): boolean {
    return isAuthenticatedRamConfig(config) && Boolean(config.UNLEASH_API_TOKEN) && Boolean(config.UNLEASH_API_URL);
}

function isUnleashReady(flagContext: UnleashFlagContext | null): boolean {
    return Boolean(flagContext?.flagsReady || flagContext?.client.isReady());
}

/**
 * Hook to check if a feature flag is enabled.
 *
 * Behavior:
 * - When Unleash client is ready: returns remote toggle value
 * - When Unleash is not configured or not ready: falls back to config F_* value
 *
 * Matches Cockpit's useFeatureFlag behavior.
 * Source: services/core/cockpit/src/composables/useFeatureFlag.ts
 */
export function useFeatureFlag(flag: FeatureFlag): boolean {
    const { config } = useConfig();
    const flagContext = useContext(FlagContext);
    const configured = isUnleashConfigured(config);

    if (configured && isUnleashReady(flagContext)) {
        return flagContext.client.isEnabled(flag) === true;
    }

    if (flag === FeatureFlag.F_USE_EMAIL_PASS_AUTH) {
        return config?.F_USE_EMAIL_PASS_AUTH === true;
    }

    if (!isAuthenticatedRamConfig(config)) {
        return false;
    }

    return config[flag] === true;
}
